import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { type Site, errText, pickText, siteApi, toMs } from './common';

type Source = 'unit' | 'container' | 'access' | 'error';
const SOURCE_LABEL: Record<Source, string> = { unit: 'systemd-egység', container: 'konténer', access: 'access (edge)', error: 'error (edge)' };
// Csak a fajtához értelmes források: egység csak átvett systemd-nél, konténer csak konténer-fajtánál.
function sourcesOf(s: Site): Source[] {
  const out: Source[] = [];
  if (s.kind === 'proxy' && s.unit) out.push('unit');
  if (s.kind === 'container') out.push('container');
  out.push('access', 'error');
  return out;
}
const SINCE = [['', 'mind'], ['15m', '15 perc'], ['2h', '2 óra'], ['1d', '1 nap']] as const;

// Access-napló összegzése (a szerver accessSummary-je) — tűrő olvasás
interface PathStat { path: string; n: number | null; p95_ms: number | null; errors: number | null }
interface ErrStat { status: string; path: string; n: number | null }
interface Summary { window: string; requests: number | null; by_status: Record<string, number>; top_paths: PathStat[]; errors: ErrStat[]; slowest: PathStat[] }
const num = (v: unknown): number | null => (v == null || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const str = (v: unknown): string => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));
function summaryOf(r: unknown): Summary | null {
  if (!r || typeof r !== 'object') return null;
  const o0 = r as Record<string, unknown>;
  const o = (o0.summary && typeof o0.summary === 'object' ? o0.summary : o0) as Record<string, unknown>;
  if (!('requests' in o) && !('by_status' in o) && !('top_paths' in o)) return null;
  const pathStat = (x: unknown): PathStat => { const p = (x ?? {}) as Record<string, unknown>; return { path: str(p.path ?? p.uri), n: num(p.n ?? p.count), p95_ms: num(p.p95_ms ?? p.p95), errors: num(p.errors) }; };
  const bs = (o.by_status && typeof o.by_status === 'object' ? o.by_status : {}) as Record<string, unknown>;
  const w = o.window;
  const win = w && typeof w === 'object' ? (() => { const x = w as Record<string, unknown>; const a = toMs(x.since ?? x.from); const b = toMs(x.until ?? x.to); return a ? `${new Date(a).toLocaleString('hu-HU')} – ${b ? new Date(b).toLocaleTimeString('hu-HU') : 'most'}` : str(w); })() : str(w);
  return {
    window: win, requests: num(o.requests),
    by_status: Object.fromEntries(Object.entries(bs).map(([k, v]) => [k, num(v) ?? 0])),
    top_paths: Array.isArray(o.top_paths) ? o.top_paths.map(pathStat) : [],
    errors: Array.isArray(o.errors) ? o.errors.map((x) => { const e = (x ?? {}) as Record<string, unknown>; return { status: str(e.status), path: str(e.path ?? e.uri), n: num(e.n ?? e.count) }; }) : [],
    slowest: Array.isArray(o.slowest) ? o.slowest.map(pathStat) : [],
  };
}
const ms = (v: number | null) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v)} ms`);

function SummaryView({ s }: { s: Summary }) {
  const tiles: [string, number | null, string][] = [['kérés', s.requests, ''], ['2xx', s.by_status['2xx'] ?? 0, 'ok'], ['3xx', s.by_status['3xx'] ?? 0, ''], ['4xx', s.by_status['4xx'] ?? 0, 'warn'], ['5xx', s.by_status['5xx'] ?? 0, 'bad']];
  return (
    <div className="a-site-sum">
      {s.window && <div className="a-site-note">Időablak: {s.window}</div>}
      <div className="a-site-sumtiles">
        {tiles.map(([k, v, c]) => <div key={k} className={`a-site-tile ${c && v ? c : ''}`}><div className="k">{k}</div><div className="v">{v ?? '—'}</div></div>)}
      </div>
      <div className="a-site-sumgrid">
        <div>
          <h4 className="a-site-h4">Leggyakoribb útvonalak</h4>
          <div className="a-site-tablewrap"><table className="a-site-table a-site-sumt">
            <thead><tr><th>Útvonal</th><th className="num">Kérés</th><th className="num">p95</th><th className="num">Hiba</th></tr></thead>
            <tbody>
              {s.top_paths.map((p, i) => <tr key={i}><td className="mono a-site-dpath">{p.path}</td><td className="num">{p.n ?? '—'}</td><td className="num">{ms(p.p95_ms)}</td><td className={`num ${p.errors ? 'badc' : ''}`}>{p.errors ?? 0}</td></tr>)}
              {!s.top_paths.length && <tr className="empty"><td colSpan={4}>Nincs kérés ebben az ablakban.</td></tr>}
            </tbody>
          </table></div>
        </div>
        <div>
          <h4 className="a-site-h4">Hibák</h4>
          <div className="a-site-tablewrap"><table className="a-site-table a-site-sumt">
            <thead><tr><th className="num">Státusz</th><th>Útvonal</th><th className="num">Db</th></tr></thead>
            <tbody>
              {s.errors.map((e, i) => <tr key={i}><td className={`num ${Number(e.status) >= 500 ? 'badc' : ''}`}>{e.status}</td><td className="mono a-site-dpath">{e.path}</td><td className="num">{e.n ?? '—'}</td></tr>)}
              {!s.errors.length && <tr className="empty"><td colSpan={3}>Nincs hiba.</td></tr>}
            </tbody>
          </table></div>
          <h4 className="a-site-h4">Leglassabbak (p95)</h4>
          <div className="a-site-tablewrap"><table className="a-site-table a-site-sumt">
            <thead><tr><th>Útvonal</th><th className="num">p95</th></tr></thead>
            <tbody>
              {s.slowest.map((p, i) => <tr key={i}><td className="mono a-site-dpath">{p.path}</td><td className="num">{ms(p.p95_ms)}</td></tr>)}
              {!s.slowest.length && <tr className="empty"><td colSpan={2}>—</td></tr>}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}

// Napló fül: forrás, sorszám, frissítés (kézzel vagy 5 mp-enként), monospace előnézet a végére görgetve.
// Access-forrásnál: szűrők (idő, status≥400, útvonal-minta) és Összegzés nézet.
export function LogView({ site }: { site: Site }) {
  const sources = sourcesOf(site);
  const [source, setSource] = useState<Source>(sources[0]!);
  const [lines, setLines] = useState(300);
  const [auto, setAuto] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [err, setErr] = useState('');
  const [at, setAt] = useState<number | null>(null);
  const [sumOn, setSumOn] = useState(false);
  const [since, setSince] = useState('');
  const [errOnly, setErrOnly] = useState(false);
  const [pathIn, setPathIn] = useState('');
  const [pathQ, setPathQ] = useState(''); // a beírt minta csendesítve (ne minden billentyűre kérjen)
  const pre = useRef<HTMLPreElement>(null);
  const stick = useRef(true);
  const access = source === 'access';
  const summaryMode = access && sumOn;

  useEffect(() => { const t = window.setTimeout(() => setPathQ(pathIn.trim()), 450); return () => clearTimeout(t); }, [pathIn]);

  const load = useCallback(() => {
    const q = new URLSearchParams({ source, lines: String(lines) });
    if (access) {
      const sn = since || (summaryMode ? '2h' : '');
      if (sn) q.set('since', sn);
      if (errOnly) q.set('status_min', '400');
      if (pathQ) q.set('path', pathQ);
      if (summaryMode) q.set('summary', '1');
    }
    siteApi<unknown>(site.id, `/log?${q.toString()}`)
      .then((r) => {
        if (summaryMode) {
          const s = summaryOf(r);
          if (!s) throw new Error('a szerver nem adott összegzést (az összegzés még nem él a szerveren?)');
          setSummary(s);
        } else setText(pickText(r, 'lines', 'text', 'log', 'output'));
        setErr(''); setAt(Date.now());
      })
      .catch((e) => setErr(errText(e, summaryMode ? 'Összegzés' : 'Napló')));
  }, [site.id, source, lines, access, since, errOnly, pathQ, summaryMode]);
  useEffect(() => { stick.current = true; setText(null); setSummary(null); load(); }, [load]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 5000);
    return () => clearInterval(t);
  }, [auto, load]);
  // Legalul görgetve, amíg a felhasználó nem görget feljebb
  useLayoutEffect(() => { const el = pre.current; if (el && stick.current) el.scrollTop = el.scrollHeight; }, [text]);

  return (
    <div className="a-site-logwrap">
      <div className="a-site-bar">
        <div className="a-site-seg">{sources.map((s) => <button key={s} type="button" className={source === s ? 'on' : ''} onClick={() => setSource(s)}>{SOURCE_LABEL[s]}</button>)}</div>
        {!summaryMode && (
          <select className="a-input a-site-sel" value={lines} onChange={(e) => setLines(Number(e.target.value))} title="Sorok száma">
            {[100, 300, 1000].map((n) => <option key={n} value={n}>{n} sor</option>)}
          </select>
        )}
        <span className="flex-1" />
        <label className="a-site-check-inline"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />auto (5 mp)</label>
        <button className="a-btn sm" onClick={load}>⟳ Frissítés</button>
      </div>
      {access && (
        <div className="a-site-bar a-site-logf">
          <label className="a-site-check-inline big"><input type="checkbox" checked={sumOn} onChange={(e) => setSumOn(e.target.checked)} />Összegzés</label>
          <div className="a-site-seg" title="Időablak">
            {SINCE.filter(([v]) => v || !summaryMode).map(([v, l]) => <button key={v} type="button" className={(since || (summaryMode ? '2h' : '')) === v ? 'on' : ''} onClick={() => setSince(v)}>{l}</button>)}
          </div>
          <label className="a-site-check-inline big"><input type="checkbox" checked={errOnly} onChange={(e) => setErrOnly(e.target.checked)} />status ≥ 400</label>
          <input className="a-input mono a-site-pathq" value={pathIn} placeholder="útvonal-minta, pl. /api/*" onChange={(e) => setPathIn(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') setPathQ(pathIn.trim()); }} />
        </div>
      )}
      {err && <div className="a-site-msg bad">{err}</div>}
      {summaryMode ? (
        summary ? <SummaryView s={summary} /> : !err ? <p className="a-sub">összegzés…</p> : null
      ) : (
        <pre ref={pre} className="a-site-log" onScroll={(e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 30; }}>
          {text == null ? (err ? '' : 'betöltés…') : text.trim() ? text : '(üres napló)'}
        </pre>
      )}
      <div className="a-site-note">{at ? `frissítve ${new Date(at).toLocaleTimeString('hu-HU')}` : ''}{auto ? ' · auto-frissítés be' : ''}</div>
    </div>
  );
}
